import Button from "/src/components/UI/Button/Button";
import { useMemo, useState } from "react";
import { RATING_BANDS } from "../../data/scorecard";
import { computeStats, filterByPeriod, formatPercent, getBand } from "../../utils/scoring";
import { CardTitle, ScoreRing, useTwoStepConfirm } from "../shared";

const PERIODS = [
    { label: "7 days", days: 7 },
    { label: "30 days", days: 30 },
    { label: "All time", days: null },
];

const OUTCOME_COLORS = {
    "Sale": "var(--green)",
    "Qualified follow-up": "var(--main)",
    "Genuine customer refusal": "var(--orange)",
    "Not commercially eligible": "#94a3b8",
    "Agent abandoned opportunity": "var(--pink)",
};

function Kpi({ icon, tone, label, value, caption, children }) {
    return (
        <div className={"ce-kpi ce-kpi--" + tone}>
            <span className="ce-kpi__icon"><i className={icon}></i></span>
            <div className="ce-kpi__body">
                <span className="ce-kpi__label">{label}</span>
                <strong className="ce-kpi__value">{value}</strong>
                <span className="ce-kpi__caption">{caption}</span>
            </div>
            {children}
        </div>
    );
}

function Donut({ items, total }) {
    const size = 156;
    const stroke = 24;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    let offset = 0;

    return (
        <div className="ce-donut" style={{ width: size, height: size }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle className="ce-ring__track" cx={size / 2} cy={size / 2} r={radius} strokeWidth={stroke} fill="none" />
                {items.filter((item) => item.count > 0).map((item) => {
                    const length = (circumference * item.count) / total;
                    const segment = (
                        <circle
                            key={item.label}
                            cx={size / 2} cy={size / 2} r={radius}
                            fill="none" stroke={item.color} strokeWidth={stroke}
                            strokeDasharray={`${Math.max(0, length - 2)} ${circumference - Math.max(0, length - 2)}`}
                            strokeDashoffset={-offset}
                            transform={`rotate(-90 ${size / 2} ${size / 2})`}
                        >
                            <title>{`${item.label}: ${item.count}`}</title>
                        </circle>
                    );
                    offset += length;
                    return segment;
                })}
            </svg>
            <div className="ce-donut__center"><strong>{total}</strong><span>calls</span></div>
        </div>
    );
}

function TrendChart({ trend }) {
    if (trend.length < 2) {
        return <p className="ce-empty-sm">The trend appears once evaluations span at least two weeks.</p>;
    }

    const width = 620;
    const height = 210;
    const pad = { left: 40, right: 14, top: 14, bottom: 30 };
    const x = (index) => pad.left + (index * (width - pad.left - pad.right)) / (trend.length - 1);
    const y = (value) => pad.top + (1 - value) * (height - pad.top - pad.bottom);

    const line = trend.map((point, index) => `${index ? "L" : "M"}${x(index).toFixed(1)},${y(point.average).toFixed(1)}`).join(" ");
    const area = `${line} L${x(trend.length - 1).toFixed(1)},${y(0)} L${x(0).toFixed(1)},${y(0)} Z`;
    const step = trend.length > 9 ? 2 : 1;

    return (
        <svg className="ce-trend" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Average score per week">
            <defs>
                <linearGradient id="ce-trend-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--main)" stopOpacity="0.28" />
                    <stop offset="100%" stopColor="var(--main)" stopOpacity="0" />
                </linearGradient>
            </defs>

            {[0, 0.55, 0.7, 0.85, 1].map((tick) => (
                <g key={tick}>
                    <line x1={pad.left} x2={width - pad.right} y1={y(tick)} y2={y(tick)} className={"ce-trend__grid" + (tick === 0.55 || tick === 0.7 || tick === 0.85 ? " is-band" : "")} />
                    <text x={pad.left - 8} y={y(tick) + 4} textAnchor="end" className="ce-trend__tick">{Math.round(tick * 100)}</text>
                </g>
            ))}

            <path d={area} fill="url(#ce-trend-fill)" />
            <path d={line} className="ce-trend__line" />

            {trend.map((point, index) => (
                <g key={point.time}>
                    <circle cx={x(index)} cy={y(point.average)} r="4.5" className="ce-trend__dot" style={{ stroke: getBand(point.average).color }}>
                        <title>{`Week of ${new Date(point.time).toLocaleDateString([], { day: "2-digit", month: "short" })}: ${formatPercent(point.average)} (${point.count} evaluation${point.count > 1 ? "s" : ""})`}</title>
                    </circle>
                    {index % step === 0 && (
                        <text x={x(index)} y={height - 8} textAnchor="middle" className="ce-trend__tick">
                            {new Date(point.time).toLocaleDateString([], { day: "2-digit", month: "short" })}
                        </text>
                    )}
                </g>
            ))}
        </svg>
    );
}

function RankedBars({ items, getColor, limit, unit = "" }) {
    const [expanded, setExpanded] = useState(false);
    const visible = expanded ? items : items.slice(0, limit);
    const max = Math.max(1, ...items.map((item) => item.count));

    return (
        <>
            <div className="ce-ranked">
                {visible.map((item) => (
                    <div key={item.label} className={"ce-ranked__row" + (item.count === 0 ? " is-zero" : "")}>
                        <span className="ce-ranked__label">{item.label}</span>
                        <div className="ce-bar ce-bar--lg"><span style={{ width: `${(item.count / max) * 100}%`, background: getColor(item) }} /></div>
                        <span className="ce-ranked__value"><b>{item.count}</b>{unit}<small>{formatPercent(item.share)}</small></span>
                    </div>
                ))}
            </div>
            {items.length > limit && (
                <button type="button" className="ce-link ce-link--block" onClick={() => setExpanded((value) => !value)}>
                    {expanded ? "Show less" : `Show all ${items.length}`}
                </button>
            )}
        </>
    );
}

function DimensionBars({ items }) {
    const [expanded, setExpanded] = useState(false);
    const visible = expanded ? items : items.slice(0, 6);

    return (
        <>
            <div className="ce-ranked">
                {visible.map(({ dimension, average, ratio }) => (
                    <div key={dimension.id} className="ce-ranked__row">
                        <span className="ce-ranked__label"><i>{String(dimension.id).padStart(2, "0")}</i>{dimension.name}</span>
                        <div className="ce-bar ce-bar--lg"><span style={{ width: `${ratio * 100}%`, background: getBand(ratio).color }} /></div>
                        <span className="ce-ranked__value"><b>{average.toFixed(1)}</b><small>/ 3</small></span>
                    </div>
                ))}
            </div>
            <button type="button" className="ce-link ce-link--block" onClick={() => setExpanded((value) => !value)}>
                {expanded ? "Show the 6 weakest only" : `Show all ${items.length} dimensions`}
            </button>
        </>
    );
}

function EvaluationStats({ log, onResetDemo }) {
    const [period, setPeriod] = useState(PERIODS[2]);
    const [confirmReset, handleReset] = useTwoStepConfirm(onResetDemo);

    const stats = useMemo(() => computeStats(filterByPeriod(log, period.days)), [log, period]);

    const outcomeItems = stats.outcomeMix.map((item) => ({ ...item, color: OUTCOME_COLORS[item.label] }));
    const leakageUsed = stats.leakageMix.filter((item) => item.count > 0);

    return (
        <div className="ce-stats">
            <div className="ce-stats__toolbar">
                <div>
                    <h3>Live summary from the evaluation log</h3>
                    <p>{stats.total} evaluation{stats.total === 1 ? "" : "s"} · {period.label.toLowerCase()}</p>
                </div>
                <div className="ce-stats__actions">
                    <div className="ce-choice" role="group" aria-label="Period">
                        {PERIODS.map((item) => (
                            <button
                                key={item.label}
                                type="button"
                                className={"ce-choice__btn" + (item === period ? " is-active tone-main" : "")}
                                onClick={() => setPeriod(item)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                    <Button
                        className="table"
                        onClick={handleReset}
                        label={<><i className="fas fa-undo"></i>{confirmReset ? "Click again to reset" : "Reset demo data"}</>}
                    />
                </div>
            </div>

            {stats.total === 0 ? (
                <section className="ce-card ce-empty">
                    <i className="fas fa-chart-pie"></i>
                    <h3>No evaluation in this period</h3>
                    <p>Submit an evaluation from the Evaluation tab, or widen the period.</p>
                </section>
            ) : (
                <>
                    <div className="ce-kpis">
                        <Kpi icon="fas fa-clipboard-check" tone="main" label="Total evaluations" value={stats.total} caption="Logged calls" />
                        <Kpi icon="fas fa-star" tone="band" label="Average score" value={formatPercent(stats.average)} caption={stats.averageBand.label}>
                            <ScoreRing value={stats.average} size={58} strokeWidth={7} color={stats.averageBand.color}><span /></ScoreRing>
                        </Kpi>
                        <Kpi icon="fas fa-trophy" tone="green" label="Strong call rate" value={formatPercent(stats.strongRate)} caption="Score at or above 70%" />
                        <Kpi icon="fas fa-exclamation-triangle" tone="pink" label="Critical call rate" value={formatPercent(stats.criticalRate)} caption="Score below 55%" />
                    </div>

                    <div className="ce-row ce-row--2-1">
                        <section className="ce-card">
                            <CardTitle icon="fas fa-chart-line" title="Average score per week" hint="Dashed lines mark the 55 / 70 / 85% rating bands" />
                            <TrendChart trend={stats.trend} />
                        </section>

                        <section className="ce-card">
                            <CardTitle icon="fas fa-layer-group" title="Score distribution" hint="Calls per rating band" />
                            <div className="ce-bands">
                                {[...RATING_BANDS].reverse().map((band) => {
                                    const count = stats.bandCounts[band.key];
                                    return (
                                        <div key={band.key} className="ce-bands__row">
                                            <span className="ce-bands__label"><i style={{ background: band.color }} />{band.label}</span>
                                            <div className="ce-bar ce-bar--lg"><span style={{ width: `${(count / stats.total) * 100}%`, background: band.color }} /></div>
                                            <span className="ce-ranked__value"><b>{count}</b><small>{formatPercent(count / stats.total)}</small></span>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    </div>

                    <div className="ce-row ce-row--1-1">
                        <section className="ce-card">
                            <CardTitle icon="fas fa-handshake" title="Outcome mix" hint="How the calls ended" />
                            <div className="ce-outcomes">
                                <Donut items={outcomeItems} total={stats.total} />
                                <ul className="ce-legend-list">
                                    {outcomeItems.map((item) => (
                                        <li key={item.label}>
                                            <i style={{ background: item.color }} />
                                            <span>{item.label}</span>
                                            <b>{item.count}</b>
                                            <small>{formatPercent(item.share)}</small>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </section>

                        <section className="ce-card">
                            <CardTitle icon="fas fa-bullseye" title="Primary coaching leakage" hint="Where calls lose the most — the coaching backlog" />
                            {leakageUsed.length ? (
                                <RankedBars items={leakageUsed} limit={7} getColor={() => "var(--pink)"} />
                            ) : <p className="ce-empty-sm">No leakage recorded yet.</p>}
                        </section>
                    </div>

                    <div className="ce-row ce-row--1-1">
                        <section className="ce-card">
                            <CardTitle icon="fas fa-seedling" title="Weakest dimensions" hint="Average score out of 3, lowest first" />
                            <DimensionBars items={stats.dimensionAverages} />
                        </section>

                        <section className="ce-card">
                            <CardTitle icon="fas fa-comments" title="Objections & urgency" hint="What customers push back on, and how urgency was handled" />
                            <RankedBars items={stats.objectionMix.filter((item) => item.count > 0)} limit={5} getColor={() => "var(--orange)"} />
                            <div className="ce-urgency">
                                {stats.urgencyMix.map((item) => (
                                    <span key={item.label} className={"ce-chip" + (item.label === "Appropriate" ? " ce-chip--green" : item.label === "Excessive" ? " ce-chip--pink" : " ce-chip--orange")}>
                                        {item.label} <b>{item.count}</b>
                                    </span>
                                ))}
                            </div>
                        </section>
                    </div>

                    <section className="ce-card">
                        <CardTitle icon="fas fa-history" title="Latest evaluations" hint="Most recent first" />
                        <div className="ce-table" role="table">
                            <div className="ce-table__row ce-table__row--head" role="row">
                                <span>Date</span><span>Agent</span><span>Manager</span><span>Outcome</span><span>Score</span>
                            </div>
                            {stats.recent.map((item) => {
                                const band = getBand(item.salesScore);
                                return (
                                    <div key={item.id} className="ce-table__row" role="row">
                                        <span>{new Date(item.date + "T12:00:00").toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" })}</span>
                                        <span className="ce-table__strong">{item.agent}</span>
                                        <span>{item.manager || "—"}</span>
                                        <span><i className="ce-dot" style={{ background: OUTCOME_COLORS[item.outcome] }} />{item.outcome}</span>
                                        <span><b className="ce-score-pill" style={{ "--pill": band.color }}>{formatPercent(item.salesScore)} · {band.label}</b></span>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                </>
            )}
        </div>
    );
}

export default EvaluationStats;
